import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import _ from "lodash";
import moment from "moment";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { rootReducersState } from "../../../reducers";

import Image from "../../../components/common/Image";
import Avatar from "../../../components/common/AvtarImage";
import { getRemainingResendHours, openUrlInNewTab } from "../../../utils/helper";
import appRoutes from "../../../routes/app.routes";
import FeedCommonFooter from "./FeedFooter";
import NavigateNextIcon from "@material-ui/icons/ChevronRight";
import ArrowBackIosIcon from "@material-ui/icons/ChevronLeft";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import BookmarkBorderIcon from "@material-ui/icons/BookmarkBorder";
import BookmarkIcon from "@material-ui/icons/Bookmark";
import Report from "../../../components/common/Report";
import VideoPlayerOverlay from "../../../components/common/VideoPlayerOverlay";
import { CircularProgress } from "@material-ui/core";

import { Button, ConfirmDialog } from "../../../components/common";
import InvitationModal from "../../../pages/SearchAndConnect/components/InvitationModal";
import searchAndConnectServices from "../../../services/searchAndConnect.services";
import {
  RESEND_CONNECTION_REQUEST_TIME_LIMIT,
  connectionRequestStatus,
} from "../../../utils/appConstants";
import RelatedArticlesSection from "./RelatedArticle";

const parseContentWithMedia = (
  htmlContent: string,
  contentMediaProcessed: Record<string, any> = {}
) => {
  if (!htmlContent || !Object.keys(contentMediaProcessed).length) {
    return [{ type: "text", content: htmlContent || "", index: 0 }];
  }
  const contentBlocks: Array<any> = [];
  let currentTextContent = "";
  let currentIndex = 0;

  const mediaPattern =
    /<div[^>]*class="[^"]*ql-media-wrapper[^"]*"[^>]*data-media-id="([^"]+)"[^>]*data-media-type="([^"]+)"[^>]*>[\s\S]*?<\/div>/g;

  let match: RegExpExecArray | null;
  let lastIndex = 0;

  while ((match = mediaPattern.exec(htmlContent)) !== null) {
    const [fullMatch, mediaId, mediaType] = match;
    const matchStart = match.index;
    const matchEnd = match.index + fullMatch.length;

    const textBefore = htmlContent.slice(lastIndex, matchStart);
    if (textBefore) currentTextContent += textBefore;

    if (currentTextContent.trim()) {
      contentBlocks.push({
        type: "text",
        content: currentTextContent.trim(),
        index: currentIndex++,
      });
      currentTextContent = "";
    }

    const mediaItem = contentMediaProcessed[mediaId];
    if (mediaItem) {
      contentBlocks.push({
        type: "media",
        mediaId,
        mediaType,
        mediaData: mediaItem,
        index: currentIndex++,
      });
    }

    lastIndex = matchEnd;
  }

  const remainingText = htmlContent.slice(lastIndex);
  if (remainingText) currentTextContent += remainingText;

  if (currentTextContent.trim()) {
    contentBlocks.push({
      type: "text",
      content: currentTextContent.trim(),
      index: currentIndex++,
    });
  }

  return contentBlocks.length
    ? contentBlocks
    : [{ type: "text", content: htmlContent, index: 0 }];
};

const formatLongDate = (isoLike: string | number | Date) =>
  new Date(isoLike).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

type FollowLike = {
  id?: number;
  status: number;
  follower_id?: number;
  cancelled_at?: string | null;
  ts?: number; 
};

const CACHE_PREFIX = "conn:"; 
const cacheKey = (viewerId: number, authorId: number) =>
  `${CACHE_PREFIX}${viewerId}:${authorId}`;

const CACHE_TTL_MS = 26 * 3600 * 1000; // 26h

const isFresh = (ts?: number) => !!ts && Date.now() - ts <= CACHE_TTL_MS;

const readCached = (viewerId: number, authorId: number): FollowLike | null => {
  try {
    const raw = localStorage.getItem(cacheKey(viewerId, authorId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as FollowLike;
    if (!isFresh(parsed.ts)) {
      localStorage.removeItem(cacheKey(viewerId, authorId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeCached = (
  viewerId: number,
  authorId: number,
  obj: FollowLike
): void => {
  try {
    localStorage.setItem(
      cacheKey(viewerId, authorId),
      JSON.stringify({ ...obj, ts: Date.now() })
    );
  } catch {}
};

const clearCached = (viewerId: number, authorId: number): void => {
  try {
    localStorage.removeItem(cacheKey(viewerId, authorId));
  } catch {}
};

const scanPendingForAuthor = (authorId: number): FollowLike | null => {
  try {
    const keys = Object.keys(localStorage);
    const pendingId =
      connectionRequestStatus.find((r) => r.status_text === "pending")
        ?.status_id || 0;

    for (const k of keys) {
      if (!k.startsWith(CACHE_PREFIX)) continue;
      const parts = k.replace(CACHE_PREFIX, "").split(":"); 
      if (parts.length !== 2) continue;
      const aId = Number(parts[1] || 0);
      if (aId !== authorId) continue;

      const raw = localStorage.getItem(k);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as FollowLike;
      if (parsed?.status === pendingId && isFresh(parsed.ts)) {
        return parsed;
      }
    }
  } catch {}
  return null;
};

interface Props {
  data: any;
  onLike: () => void;
  onCopy: () => void;
  onReport?: () => void;
  onBookmark?: () => void;
}

const ArticleDetailsView: React.FC<Props> = ({
  data,
  onLike,
  onCopy,
  onReport,
  onBookmark,
}) => {
  const session = useSelector((s: rootReducersState) => s.session);
  const currentUserId = _.get(session, "currentUser.id", 0);
  const authorId = _.get(data, "user.id", 0);
  const isAuthor =
    !!_.get(data, "is_author", false) || authorId === currentUserId;
  const isConnectedWithAuthor =
    !!Number(_.get(data, "connected_with_author", 0)) ||
    !!_.get(data, "connected_with_author", false);
  const readingMinutes = useMemo(() => {
    const plain = (data?.content || "").replace(/<[^>]+>/g, "");
    const words = plain.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200));
  }, [data?.content]);

  const authorName = useMemo(
    () =>
      `${_.get(data, "user.first_name", "")} ${_.get(
        data,
        "user.last_name",
        ""
      )}`.trim(),
           // eslint-disable-next-line
    [data?.user]
  );
  const authorImg =
    _.get(data, "post_user_image") || _.get(data, "user.profile_image");

  /* kebab/Report */
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [openReport, setOpenReport] = useState(false);
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);
  
   // eslint-disable-next-line
  const contentMediaProcessed = data?.content_media_processed || {};
  console.log("[ArticleView] content_media_processed:", contentMediaProcessed);
  console.log("[ArticleView] content snippet:", (data?.content || "").slice(0, 500));
  const contentBlocks = useMemo(
    () => parseContentWithMedia(data?.content || "", contentMediaProcessed),
    [data?.content, contentMediaProcessed]
  );

  const allMediaItems = useMemo(() => {
    const fromContent = Object.keys(contentMediaProcessed).map((id) => {
      const item = contentMediaProcessed[id];
      return {
        url: item.signedUrl,
        type: item.type === "image" ? "photo" : item.type,
        source: "content_media",
        mediaId: id,
        thumbnail: item.thumbnailSignedUrl || null,
      };
    });
    const fromPostMedia = (data.post_media || []).map((pm: any) => ({
      url: pm.media_type === "image" ? pm.media_thumbnail : pm.media_original,
      type: pm.media_type === "image" ? "photo" : pm.media_type,
      source: "post_media",
      mediaId: null,
      thumbnail: pm.media_thumbnail || null,
    }));
    const cover: any[] = [];
    if (data.cover_video) {
      cover.push({
        url: data.cover_video,
        type: "video",
        source: "cover_video",
        mediaId: "cover_video",
        thumbnail: data.cover_image || null,
      });
    } else if (data.cover_image) {
      cover.push({
        url: data.cover_image,
        type: "photo",
        source: "cover_image",
        mediaId: "cover_image",
        thumbnail: data.cover_image || null,
      });
    }
    return [...fromPostMedia, ...fromContent, ...cover];
  }, [contentMediaProcessed, data.post_media, data.cover_video, data.cover_image]);

  const [previewIndex, setPreviewIndex] = useState(-1);
  const [photoLoaded, setPhotoLoaded] = useState(false);

  const currentPreview = previewIndex >= 0 ? allMediaItems[previewIndex] : null;
  const canGoPrev = previewIndex > 0;
  const canGoNext =
    previewIndex >= 0 && previewIndex < allMediaItems.length - 1;

  const openLightboxAt = (i: number) => {
    setPreviewIndex(i);
    setPhotoLoaded(allMediaItems[i]?.type === "photo");
  };

  const closeLightbox = () => {
    setPreviewIndex(-1);
    setPhotoLoaded(false);
  };

  const goToPreview = (nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= allMediaItems.length) return;
    setPreviewIndex(nextIndex);
    setPhotoLoaded(allMediaItems[nextIndex]?.type === "photo");
  };

  const goPrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    goToPreview(previewIndex - 1);
  };

  const goNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    goToPreview(previewIndex + 1);
  };
  const findContentMediaIndex = (mediaId: string) =>
    allMediaItems.findIndex(
      (m) => m.source === "content_media" && (m as any).mediaId === mediaId
    );

  const handleMentionClick = (e: React.MouseEvent) => {
    const mentionSpan = (e.target as HTMLElement).closest?.(".mention");
    if (!mentionSpan) return;
    const el = mentionSpan as HTMLElement;
    const slug = el.dataset?.slug;
    const isCompany =
      el.dataset?.is_company === "true" || el.dataset?.isCompany === "true";
    if (slug) {
      e.preventDefault();
      e.stopPropagation();
      const url = isCompany
        ? appRoutes.CompanyPublicView.generatePath(slug)
        : appRoutes.candidatePublicProfile.generateFullPath(slug);
      openUrlInNewTab(url);
    }
  };

  const coverIndex = useMemo(
    () =>
      allMediaItems.findIndex(
        (m) => m.source === "cover_video" || m.source === "cover_image"
      ),
    [allMediaItems]
  );

  const [authorFollow, setAuthorFollow] = useState<FollowLike | null>(
    _.get(data, "author_follow", null)
  );

  const [cancelSentRequestShow, setCancelSentRequestShow] = useState(false);
  const [invitationOpen, setInvitationOpen] = useState(false);

  const statusTextFromId = (statusId?: number) =>
    connectionRequestStatus.find((row) => row.status_id === statusId)
      ?.status_text;

  const PENDING_ID =    connectionRequestStatus.find((r) => r.status_text === "pending")?.status_id || 0;

  useEffect(() => {
    if (isAuthor || !authorId) return;
    if (authorFollow) return;

    if (currentUserId) {
      const cached = readCached(currentUserId, authorId);
      if (cached?.status) {
        setAuthorFollow(cached);
        return;
      }
    }
    const scan = scanPendingForAuthor(authorId);
    if (scan) {
      setAuthorFollow(scan); 
      return;
    }

     if (isConnectedWithAuthor) {
      const acceptedId =
        connectionRequestStatus.find((r) => r.status_text === "accepted")
          ?.status_id || 0;
      setAuthorFollow({ status: acceptedId, ts: Date.now() });
    }
      // eslint-disable-next-line
  }, [currentUserId, authorId, isAuthor, isConnectedWithAuthor, authorFollow]);


  useEffect(() => {
    if (isAuthor || !authorId || !currentUserId) return;

    (async () => {
      try {
        const res = await searchAndConnectServices.getConnectionStatus({
          id: authorId,
        });
        const follow = _.get(res, "data.data", null);
        if (follow) {
          const normalized: FollowLike = {
            id: follow.id,
            status: follow.status,
            follower_id: follow.follower_id,
            cancelled_at: follow.cancelled_at || null,
            ts: Date.now(),
          };
          setAuthorFollow(normalized);
          writeCached(currentUserId, authorId, normalized);
        } else if (isConnectedWithAuthor) {
          const acceptedId =
            connectionRequestStatus.find((r) => r.status_text === "accepted")
              ?.status_id || 0;
          const accepted = {
            id: 0,
            status: acceptedId,
            follower_id: currentUserId,
            cancelled_at: null,
            ts: Date.now(),
          };
          setAuthorFollow(accepted);
          writeCached(currentUserId, authorId, accepted);
        } else {
          // no relation
          setAuthorFollow(null);
          clearCached(currentUserId, authorId);
        }
      } catch {
        // If the status endpoint is down or slow, we KEEP the optimistic UI.
      }
    })();
      // eslint-disable-next-line
  }, [currentUserId, authorId, isAuthor]);

  const handleInviteSent = async ({ note }) => {
    try {
          const optimistic: FollowLike = {
        id: 0,
        status: PENDING_ID,
        follower_id: currentUserId || undefined,
        cancelled_at: null,
        ts: Date.now(),
      };
      setAuthorFollow(optimistic);
      if (currentUserId) writeCached(currentUserId, authorId, optimistic);

      // Server
      const res = await searchAndConnectServices.sendInvitation({
        note: note || "",
        id: authorId,
      });

      // Reconcile with real id if server returns one
      const realId = _.get(res, "data.data.id", 0);
      if (realId) {
        const merged = { ...optimistic, id: realId, ts: Date.now() };
        setAuthorFollow(merged);
        if (currentUserId) writeCached(currentUserId, authorId, merged);
      }
    } finally {
      setInvitationOpen(false);
    }
  };

  // Withdraw -> Wait 24h (optimistic + cache) with native confirm fallback
  const handleCancelSentRequest = async () => {
    try {
      const id = _.get(authorFollow, "id", 0);
      // Optimistic state immediately (even if id missing)
      const cancelledId =
        connectionRequestStatus.find(
          (r) => r.status_text === "cancelled_24_hours"
        )?.status_id || 0;

      const updated: FollowLike = {
        ...authorFollow,
        status: cancelledId,
        cancelled_at: moment.utc().format("YYYY-MM-DD HH:mm:ss"),
        ts: Date.now(),
      };
      setAuthorFollow(updated);
      if (currentUserId) writeCached(currentUserId, authorId, updated);

      // Hit server only if we have a relation id
      if (id) {
        await searchAndConnectServices.updateCandidateConnection({
          id,
          type: "cancelled",
          reject_reason: "",
        });
      }
    } finally {
      setCancelSentRequestShow(false);
    }
  };

  /* Render connect/withdraw/wait */
  const renderConnectButton = () => {
    if (isAuthor || !authorId) return <></>;

    // No follow object known yet
    if (!authorFollow) {
      if (isConnectedWithAuthor) return <></>; // accepted from BE hint
      return (
        <Button color="primary" onClick={() => setInvitationOpen(true)}>
          Connect
        </Button>
      );
    }

    const statusText = statusTextFromId(authorFollow?.status);

    switch (statusText) {
      case "accepted":
        return <></>;

      case "rejected":
        return (
          <Button color="secondary" className="text-uppercase" disabled>
            Rejected
          </Button>
        );

      case "pending":
        // ALWAYS show Withdraw for pending
        return (
          <Button
            type="button"
            color="secondary"
            className="btn-cancel-request"
            onClick={() => {
              // Try your modal first
              setCancelSentRequestShow(true);
            }}
          >
            Withdraw
          </Button>
        );

      case "cancelled_24_hours": {
        const hoursLeft = getRemainingResendHours(authorFollow?.cancelled_at);
        if (hoursLeft <= 0) {
          return (
            <Button color="primary" onClick={() => setInvitationOpen(true)}>
              Connect
            </Button>
          );
        }
        return (
          <Button disabled className="w-mx-content">
            Wait {hoursLeft} {hoursLeft === 1 ? "hour" : "hours"}
          </Button>
        );
      }

      default:
        return (
          <Button color="primary" onClick={() => setInvitationOpen(true)}>
            Connect
          </Button>
        );
    }
  };

  return (
    <article className="article-page">
      <Helmet>
        <title>{data?.title || "Article"} • employHER</title>
      </Helmet>

      {/* Header */}
      <div className="article-header">
        <h3 className="article-title">{data?.title}</h3>

        <div className="article-bar">
          {/* left: author */}
          <div
            className="meta-left"
            onClick={() => {
              if (data?.user?.slug) {
                openUrlInNewTab(
                  appRoutes.candidatePublicProfile.generateFullPath(
                    data.user.slug
                  )
                );
              }
            }}
          >
            <Avatar src={authorImg} className="author-avatar" />
            <div className="author-block">
              <div className="author-name">{authorName || "—"}</div>
              <div className="author-sub">
                {formatLongDate(data?.created_at)} • {readingMinutes} min read
              </div>
            </div>
                {renderConnectButton()}
          </div>

          {/* right: actions */}
          <div className="meta-right" ref={menuRef}>
        

            <button
              aria-label={data?.bookmarked ? "Remove bookmark" : "Bookmark"}
              className={`bookmark-btn ${data?.bookmarked ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onBookmark && onBookmark();
              }}
              title={data?.bookmarked ? "Remove bookmark" : "Save to bookmarks"}
            >
              {data?.bookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
            </button>

            <button
              aria-label="More"
              className="kebab-btn"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((s) => !s);
              }}
            >
              <MoreVertIcon />
            </button>

            {menuOpen && (
              <div className="kebab-menu" role="menu">
                <button
                  role="menuitem"
                  className="kebab-item"
                  onClick={() => {
                    onCopy();
                    setMenuOpen(false);
                  }}
                >
                  Copy link
                </button>
                <button
                  role="menuitem"
                  className="kebab-item"
                  onClick={() => {
                    setOpenReport(true);
                    setMenuOpen(false);
                  }}
                >
                  Report an article
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      {openReport && (
        <Report
          openReports={openReport}
          referenceId={data?.id}
          type="article"
          titleText="Report this article"
          onClose={() => setOpenReport(false)}
        />
      )}

      {/* Cover */}
      {(data?.cover_image || data?.cover_video) && (
        <div className="article-cover">
          {data?.cover_video ? (
            <div className="article-cover-video">
              <div
                className="video-cover"
                style={{ position: "relative", cursor: "pointer" }}
                onClick={() => coverIndex >= 0 && openLightboxAt(coverIndex)}
              >
                <Image src={data.cover_image} alt="cover" />
                <span className="play-icon-cover">
                  <i className="fa fa-play"></i>
                </span>
              </div>
            </div>
          ) : (
            <div
              className="article-cover-image"
              style={{ cursor: coverIndex >= 0 ? "pointer" : "default" }}
              onClick={() => coverIndex >= 0 && openLightboxAt(coverIndex)}
            >
              <Image src={data.cover_image} alt="cover" />
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <section
        className="article-content wysiwyg-data"
        onClick={handleMentionClick}
      >
        {contentBlocks.map((block) => {
          if (block.type === "text") {
            return (
              <div
                key={`text-${block.index}`}
                className="content-text-block"
                dangerouslySetInnerHTML={{ __html: block.content }}
              />
            );
          }
          if (block.type === "media") {
            const mediaItem = block.mediaData;
            const i = findContentMediaIndex(block.mediaId);
            const isImage = mediaItem.type === "image";

            return (
              <div
                key={`media-${block.mediaId}`}
                className="content-media-block article-img-wrapper"
              >
                <div
                  className={`img-wrapper content-media-item ${
                    isImage ? "is-photo" : "is-video"
                  }`}
                  onClick={() => i >= 0 && openLightboxAt(i)}
                  style={{ cursor: "pointer" }}
                >
                  {isImage ? (
                    <Avatar type="post" src={mediaItem.signedUrl} />
                  ) : (
                    <div className="video-cover">
                      <span className="play-icon-cover">
                        <i className="fa fa-play"></i>
                      </span>
                      <Avatar
                        type="post"
                        src={mediaItem.thumbnailSignedUrl || mediaItem.signedUrl}
                        className="avatar-thumbnail"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          }
          return null;
        })}
      </section>

      {/* Footer */}
      <FeedCommonFooter
        data={data}
        postLike={onLike}
        handleCopyPostLink={onCopy}
        defaultShowComments={true}
      />

      {/* Media Preview */}
      {currentPreview?.type === "video" &&
        createPortal(
          <>
            <VideoPlayerOverlay
              key={currentPreview.url}
              open
              onClose={closeLightbox}
              videoUrl={currentPreview.url}
              videoThumbnail={currentPreview.thumbnail || undefined}
            />
            {canGoPrev && (
              <button
                type="button"
                className="feed-preview-nav feed-preview-nav--prev"
                aria-label="Previous media"
                onClick={goPrev}
              >
                <ArrowBackIosIcon />
              </button>
            )}
            {canGoNext && (
              <button
                type="button"
                className="feed-preview-nav feed-preview-nav--next"
                aria-label="Next media"
                onClick={goNext}
              >
                <NavigateNextIcon />
              </button>
            )}
          </>,
          document.body
        )}

      {currentPreview?.type === "photo" &&
        createPortal(
          <div className="media-preview-overlay" onClick={closeLightbox}>
            {canGoPrev && (
              <button
                type="button"
                className="feed-preview-nav feed-preview-nav--prev"
                aria-label="Previous media"
                onClick={goPrev}
              >
                <ArrowBackIosIcon />
              </button>
            )}

            <div
              className={`media-preview-content ${
                photoLoaded ? "is-loading" : ""
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {photoLoaded && (
                <div className="media-preview-loader" aria-live="polite">
                  <CircularProgress size={40} style={{ color: "#fff" }} />
                </div>
              )}
              <img
                src={currentPreview.url}
                alt="preview"
                onLoad={() => setPhotoLoaded(false)}
                style={{ maxHeight: "82vh", maxWidth: "92vw" }}
              />
            </div>

            {canGoNext && (
              <button
                type="button"
                className="feed-preview-nav feed-preview-nav--next"
                aria-label="Next media"
                onClick={goNext}
              >
                <NavigateNextIcon />
              </button>
            )}
            <button
              type="button"
              className="video-card-player-close"
              onClick={closeLightbox}
              aria-label="Close"
            >
              &times;
            </button>
          </div>,
          document.body
        )}

      {/* Connect modals */}
      <InvitationModal
        open={invitationOpen}
        payload={{ id: authorId, ..._.get(data, "user", {}) }}
        onInviteSent={handleInviteSent}
        handleOnClose={() => setInvitationOpen(false)}
      />
      <ConfirmDialog
        visible={cancelSentRequestShow}
        title="Cancel sent request"
        bodyText={`Request to cancel will not allow to reconnect with this person for up to ${RESEND_CONNECTION_REQUEST_TIME_LIMIT} hours.`}
        onCancel={() => setCancelSentRequestShow(false)}
        onConfirm={handleCancelSentRequest}
        confirmText="Okay"
      />
      <RelatedArticlesSection
  postId={data?.id}
  limit={3}
  title="Related articles"
/>
      
    </article>
  );

};

export default ArticleDetailsView;

